/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

export const basePath = '/';

const applyBasePathTo = (path: string) => `${basePath}${path}`;

/*
 * Usage:
 * paginatedPath(siteNotificationsPath(':siteName'))(':pageNum')
 */

export const paginatedPath = (path: string) => (page: string | number) => `${path}/page/${page}`;

export const doublePaginatedPath = (path: string) => (page1: string | number) => (page2: string | number) =>
  `${path}/page/${page1}/${page2}`;

export const reverseDoublePaginatedPath = (path: string) => (page2: string | number) => (page1: string | number) =>
  `${path}/page/${page1}/${page2}`;

// Shared paths

export const signInPath = applyBasePathTo('sign-in');

export const signOutPath = applyBasePathTo('sign-out');

export const contactUsPath = applyBasePathTo('contact-us');

export const contactUsSuccessPath = applyBasePathTo('contact-us-success');

export const restorePasswordPath = applyBasePathTo('restore-password');

export const recoverPasswordPath = applyBasePathTo('recover-password');

export const restorePasswordSuccessPath = applyBasePathTo('restore-password-success');

export const registrationPath = applyBasePathTo('registration');

export const registrationSuccessPath = applyBasePathTo('registration-success');

export const notFoundPath = applyBasePathTo('not-found');

export const tooManyRequestsErrorPath = applyBasePathTo('too-many-requests');

export const installationPath = applyBasePathTo('installation');

export const helpPath = applyBasePathTo('help');

export const baseErrorPath = applyBasePathTo('error');

export const termsConditionsPath = applyBasePathTo('terms-of-use');

export const dataPrivacyPath = applyBasePathTo('data-privacy');

export const imprintPath = applyBasePathTo('imprint');

export const wechatCallbackPath = applyBasePathTo('wechat/callback');

export const wechatInvitePath = applyBasePathTo('wechat/invite');

export const wechatAccountChoicePath = applyBasePathTo('wechat/account-choice');

export const wechatLoginLinkPath = applyBasePathTo('wechat/login-link');

// Logged in paths

export const baseAppPath = applyBasePathTo('app');

export const profileRoutePath = 'profile';
export const profilePath = `${baseAppPath}/${profileRoutePath}`;

export const appErrorRoutePath = 'error';
export const errorPath = `${baseAppPath}/${appErrorRoutePath}`;

export const profilePreferencesRoutePath = 'preferences';
export const profilePreferencesPath = `${profilePath}/${profilePreferencesRoutePath}`;

export const profilePushNotificationsRoutePath = 'push-notifications';
export const profilePushNotificationsPath = `${profilePath}/${profilePushNotificationsRoutePath}`;

export const profilePermissionsRoutePath = 'permissions';
export const profilePermissionsPath = `${profilePath}/${profilePermissionsRoutePath}`;

export const profileChangePasswordRoutePath = 'change-password';
export const profileChangePasswordPath = `${profilePath}/${profileChangePasswordRoutePath}`;

export const profileSecurityAndPrivacyRoutePath = 'terms-of-use';
export const profileSecurityAndPrivacyPath = `${profilePath}/${profileSecurityAndPrivacyRoutePath}`;

export const profileHelpCenterRoutePath = 'help-center';
export const profileHelpCenterPath = `${profilePath}/${profileHelpCenterRoutePath}`;

export const profileIntegratorImportRoutePath = 'integrator-import';
export const profileIntegratorImportPath = `${profilePath}/${profileIntegratorImportRoutePath}`;

export const profileDataPrivacyRoutePath = 'data-privacy';
export const profileDataPrivacyPath = `${profilePath}/${profileDataPrivacyRoutePath}`;

export const profileImprintRoutePath = 'imprint';
export const profileImprintPath = `${profilePath}/${profileImprintRoutePath}`;

export const profileAboutPath = `${profilePath}/about`;

export const profileWechatRoutePath = 'wechat';
export const profileWechatPath = `${profilePath}/${profileWechatRoutePath}`;

export const profileWechatCallbackRoutePath = `${profileWechatRoutePath}/callback`;
export const profileWechatCallbackPath = `${profilePath}/${profileWechatCallbackRoutePath}`;

export const sitesRoutePath = 'sites';
export const sitesPath = `${baseAppPath}/${sitesRoutePath}`;

export const sitesMapRoutePath = 'map';
export const sitesMapPath = `${sitesPath}/${sitesMapRoutePath}`;

export const siteNotFound = `${sitesPath}/notFound`;

export const siteBasePath = (site: string) => `${sitesPath}/${site}`;

export const siteCreatingRoutePath = 'new';
export const siteCreatingPath = (id: string) => `${siteBasePath(id)}/${siteCreatingRoutePath}`;

export const siteDashboardRoutePath = 'dashboard';
export const siteDashboardPath = (site: string) => `${siteBasePath(site)}/${siteDashboardRoutePath}`;

export const siteEditRoutePath = 'edit';
export const siteEditPath = (site: string) => `${siteBasePath(site)}/${siteEditRoutePath}`;

export const siteAlertsRoutePath = 'alerts';
export const siteAlertsPath = (site: string) => `${siteBasePath(site)}/${siteAlertsRoutePath}`;

export const siteNotificationsRoutePath = 'notifications';
export const siteNotificationsPath = (site: string) => `${siteBasePath(site)}/${siteNotificationsRoutePath}`;

export const siteDevicesRoutePath = 'monitors';
export const siteDevicesPath = (site: string) => `${siteBasePath(site)}/${siteDevicesRoutePath}`;

export const siteDevicesHourPath = (site: string, timestamp: string) => `${siteDevicesPath(site)}/hour/${timestamp}`;

export const siteDevicesDayPath = (site: string, timestamp: string) => `${siteDevicesPath(site)}/day/${timestamp}`;

export const siteDevicesWeekPath = (site: string, timestamp: string) => `${siteDevicesPath(site)}/week/${timestamp}`;

export const siteDevicesMonthPath = (site: string, timestamp: string) => `${siteDevicesPath(site)}/month/${timestamp}`;

export const siteUsersRoutePath = 'users';
export const siteUsersPath = (site: string) => `${siteBasePath(site)}/${siteUsersRoutePath}`;

export const siteExportsRoutePath = 'exports';
export const siteExportsPath = (site: string) => `${siteBasePath(site)}/${siteExportsRoutePath}`;

export const siteBatchesRoutePath = 'batches';
export const siteBatchesPath = (site: string) => `${siteBasePath(site)}/${siteBatchesRoutePath}`;

export const siteSettingsPath = (site: string) => `${siteBasePath(site)}/settings`;

export const siteAirspacePath = (site: string, airspace: string) => `${siteBasePath(site)}/airspaces/${airspace}`;

export const siteAirspaceDashboardPath = (site: string, airspace: string) =>
  `${siteAirspacePath(site, airspace)}/dashboard`;

export const siteAirspaceRecommendationsPath = (site: string, airspace: string) =>
  `${siteAirspacePath(site, airspace)}/recommendations`;

export const siteAirspaceDevicesRoutePath = 'devices';
export const siteAirspaceDevicesPath = (site: string, airspace: string) =>
  `${siteAirspacePath(site, airspace)}/${siteAirspaceDevicesRoutePath}`;

export const siteAirspaceGraphsRoutePath = 'graphs';
export const siteAirspaceGraphsPath = (site: string, airspace: string) =>
  `${siteAirspacePath(site, airspace)}/${siteAirspaceGraphsRoutePath}`;

export const siteAirspaceLogbookRoutePath = 'logbook';
export const siteAirspaceLogbookPath = (site: string, airspace: string) =>
  `${siteAirspacePath(site, airspace)}/${siteAirspaceLogbookRoutePath}`;

export const siteAirspaceAudioRoutePath = 'audio';
export const siteAirspaceAudioPath = (site: string, airspace: string) =>
  `${siteAirspacePath(site, airspace)}/${siteAirspaceAudioRoutePath}`;

export const siteAirspaceExportsRoutePath = 'exports';
export const siteAirspaceExportsPath = (site: string, airspace: string) =>
  `${siteAirspacePath(site, airspace)}/${siteAirspaceExportsRoutePath}`;

export const appNotFoundRoutePath = 'not-found';
export const appNotFoundPath = `${baseAppPath}/${appNotFoundRoutePath}`;

export const appForbiddenRoutePath = 'forbidden';
export const appForbiddenPath = `${baseAppPath}/${appForbiddenRoutePath}`;

export const appServerErrorRoutePath = 'server-error';
export const appServerErrorPath = `${baseAppPath}/${appServerErrorRoutePath}`;

// Markets

export const appStoreLink = 'https://itunes.apple.com/us/app/soundtalks/id1437633894';

export const googlePlayLink = 'https://play.google.com/store/apps/details?id=com.soundtalks.android';

// Admin dashboard

export const baseAdminPath = applyBasePathTo('admin');

export const adminSitesRoutePath = 'sites';
export const adminSitesPath = `${baseAdminPath}/${adminSitesRoutePath}`;

export const adminSitesDashboardPath = `${adminSitesPath}/dashboard`;

export const adminSitesListPath = `${adminSitesPath}/list`;

export const adminSitePath = (site: string) => `${adminSitesPath}/${site}`;

export const adminSiteCreateEmptyRoutePath = 'empty';
export const adminSiteCreateEmptyPath = `${adminSitesPath}/${adminSiteCreateEmptyRoutePath}`;

export const adminSiteEditPath = (site: string) => `${adminSitesPath}/${site}/edit`;

export const adminSiteDashboardRoutePath = 'dashboard';
export const adminSiteDashboardPath = (site: string) => `${adminSitePath(site)}/${adminSiteDashboardRoutePath}`;

export const adminSiteInfoRoutePath = 'info';
export const adminSiteInfoPath = (site: string) => `${adminSitePath(site)}/${adminSiteInfoRoutePath}`;

export const adminSiteRecommendationsRoutePath = 'recommendations';
export const adminSiteRecommendationsPath = (site: string) =>
  `${adminSitePath(site)}/${adminSiteRecommendationsRoutePath}`;

export const adminSiteInfoDevicesPath = (site: string) => `${adminSiteInfoPath(site)}/devices`;

export const adminSiteAlertsNotificationsRoutePath = 'alerts-notifications';
export const adminSiteAlertsNotificationsPath = (site: string) =>
  `${adminSitePath(site)}/${adminSiteAlertsNotificationsRoutePath}`;

export const adminSiteAlertsRoutePath = 'alerts';
export const adminSiteAlertsListPath = (site: string) =>
  `${adminSiteAlertsNotificationsPath(site)}/${adminSiteAlertsRoutePath}`;

export const adminSiteNotificationsRoutePath = 'notifications';
export const adminSiteNotificationsListPath = (site: string) =>
  `${adminSiteAlertsNotificationsPath(site)}/${adminSiteNotificationsRoutePath}`;

export const adminSitePushNotificationsRoutePath = 'push-notifications';
export const adminSitePushNotificationsListPath = (site: string) =>
  `${adminSiteAlertsNotificationsPath(site)}/${adminSitePushNotificationsRoutePath}`;

export const adminSiteLocationsRoutePath = 'locations';
export const adminSiteLocationsPath = (site: string) => `${adminSitePath(site)}/${adminSiteLocationsRoutePath}`;

export const adminSiteGraphsRoutePath = 'graphs';
export const adminSiteGraphsPath = (site: string) => `${adminSitePath(site)}/${adminSiteGraphsRoutePath}`;

export const adminSiteUsersRoutePath = 'users';
export const adminSiteUsersPath = (site: string) => `${adminSitePath(site)}/${adminSiteUsersRoutePath}`;

export const adminSiteBatchesRoutePath = 'batches';
export const adminSiteBatchesPath = (site: string) => `${adminSitePath(site)}/${adminSiteBatchesRoutePath}`;

export const adminSiteGroupsRoutePath = 'groups';
export const adminSiteGroupsPath = (site: string) => `${adminSitePath(site)}/${adminSiteGroupsRoutePath}`;

export const adminSiteAlertExplainRoutePath = 'alert-explain';
export const adminSiteAlertExplainPath = (site: string) => `${adminSitePath(site)}/${adminSiteAlertExplainRoutePath}`;

export const adminSiteGraphsDataAvailabilityRoutePath = 'data-availability';
export const adminSiteGraphsDataAvailabilityPath = (site: string) =>
  `${adminSiteGraphsPath(site)}/${adminSiteGraphsDataAvailabilityRoutePath}`;

export const adminSiteGraphsDataUsageRoutePath = 'data-usage';
export const adminSiteGraphsDataUsagePath = (site: string) =>
  `${adminSiteGraphsPath(site)}/${adminSiteGraphsDataUsageRoutePath}`;

export const adminSiteGraphsMonitorStatusPath = (site: string) => `${adminSiteGraphsPath(site)}/monitor-status`;

export const adminSiteGraphsRdiPath = (site: string) => `${adminSiteGraphsPath(site)}/rdi`;

export const adminSiteGraphsRdiTemperatureHumidityRoutePath = 'rdi-temperature-humidity';
export const adminSiteGraphsRdiTemperatureHumidityPath = (site: string) =>
  `${adminSiteGraphsPath(site)}/${adminSiteGraphsRdiTemperatureHumidityRoutePath}`;

export const adminSiteGraphsBandwidthPath = (site: string) => `${adminSiteGraphsPath(site)}/bandwidth`;

export const adminSiteGraphsDownloadUploadPath = (site: string) => `${adminSiteGraphsPath(site)}/download-upload`;

export const adminDevicesRoutePath = 'devices';
export const adminDevicesPath = `${baseAdminPath}/${adminDevicesRoutePath}`;

export const adminDevicesDashboardRoutePath = 'dashboard';
export const adminDevicesDashboardPath = `${adminDevicesPath}/${adminDevicesDashboardRoutePath}`;

export const adminDevicesGatewaysPath = `${adminDevicesPath}/gateways`;

export const adminDevicesListPath = `${adminDevicesPath}/list`;

export const adminDevicePath = (device: string) => `${adminDevicesPath}/${device}`;

export const adminDeviceDashboardPath = (device: string) => `${adminDevicePath(device)}/dashboard`;

export const adminDeviceInfoRoutePath = 'info';
export const adminDeviceInfoPath = (device: string) => `${adminDevicePath(device)}/${adminDeviceInfoRoutePath}`;

export const adminDeviceGraphsRoutePath = 'graphs';
export const adminDeviceGraphsPath = (device: string) => `${adminDevicePath(device)}/${adminDeviceGraphsRoutePath}`;

export const adminDeviceHeatmapsPath = (device: string) => `${adminDevicePath(device)}/heatmaps`;

export const adminDeviceCoughsRoutePath = 'coughs';
export const adminDeviceCoughsPath = (device: string) => `${adminDevicePath(device)}/${adminDeviceCoughsRoutePath}`;

export const adminDeviceAudioSnippetsRoutePath = 'audio-snippets';
export const adminDeviceAudioSnippetsPath = (device: string) =>
  `${adminDevicePath(device)}/${adminDeviceAudioSnippetsRoutePath}`;

export const adminDeviceAudioPath = (device: string) => `${adminDevicePath(device)}/audio`;

export const adminDeviceUnprocessedPath = (device: string) => `${adminDevicePath(device)}/unprocessed`;

export const adminDeviceGraphsDataAvailabilityRoutePath = 'data-availability';
export const adminDeviceGraphsDataAvailabilityPath = (device: string) =>
  `${adminDeviceGraphsPath(device)}/${adminDeviceGraphsDataAvailabilityRoutePath}`;

export const adminDeviceGraphsDataUsageRoutePath = 'data-usage';
export const adminDeviceGraphsDataUsagePath = (device: string) =>
  `${adminDeviceGraphsPath(device)}/${adminDeviceGraphsDataUsageRoutePath}`;

export const adminDeviceGraphsMonitorStatusPath = (device: string) => `${adminDeviceGraphsPath(device)}/monitor-status`;

export const adminDeviceGraphsRdiPath = (device: string) => `${adminDeviceGraphsPath(device)}/rdi`;

export const adminDeviceGraphsRdiTemperatureHumidityRoutePath = 'rdi-temperature-humidity';
export const adminDeviceGraphsRdiTemperatureHumidityPath = (device: string) =>
  `${adminDeviceGraphsPath(device)}/${adminDeviceGraphsRdiTemperatureHumidityRoutePath}`;

export const adminDeviceGraphsBandwidthPath = (device: string) => `${adminDeviceGraphsPath(device)}/bandwidth`;

export const adminDeviceGraphsDownloadUploadPath = (device: string) =>
  `${adminDeviceGraphsPath(device)}/download-upload`;

export const adminUsersRoutePath = 'users';
export const adminUsersPath = `${baseAdminPath}/${adminUsersRoutePath}`;

export const adminCompaniesRoutePath = 'companies';
export const adminCompaniesPath = `${baseAdminPath}/${adminCompaniesRoutePath}`;

export const adminCompaniesListPath = `${adminCompaniesPath}/list`;

export const adminCompanyDetailsPath = (company: string) => `${adminCompaniesPath}/company-details/${company}`;

export const adminAlertRulesRoutePath = 'alert-rules';
export const adminAlertRulesPath = `${baseAdminPath}/${adminAlertRulesRoutePath}`;

export const adminAlertRulesListPath = `${adminAlertRulesPath}/list`;

export const adminAlertRuleCreatePath = `${adminAlertRulesPath}/create`;

export const adminAlertRuleDetailsPath = (id: string) => `${adminAlertRulesPath}/${id}`;

export const adminAlertRuleEditPath = (id: string) => `${adminAlertRuleDetailsPath(id)}/edit`;

export const adminAlertRuleVersionsPath = (id: string) => `${adminAlertRuleDetailsPath(id)}/versions`;

// Escalation policies are a second tab of the alert-rules admin, not their own sidepanel section:
// a ladder only means anything to a rule that points at it.
export const adminEscalationPoliciesRoutePath = 'escalation-policies';
export const adminEscalationPoliciesPath = `${adminAlertRulesPath}/${adminEscalationPoliciesRoutePath}`;
export const adminEscalationPoliciesListPath = `${adminEscalationPoliciesPath}/list`;
export const adminEscalationPolicyCreatePath = `${adminEscalationPoliciesPath}/create`;
export const adminEscalationPolicyDetailsPath = (id: string) => `${adminEscalationPoliciesPath}/${id}`;

export const adminSuppliersRoutePath = 'suppliers';
export const adminSuppliersPath = `${baseAdminPath}/${adminSuppliersRoutePath}`;

export const adminSuppliersListPath = `${adminSuppliersPath}/list`;

export const adminSupplierDetailsPath = (suppler: string) => `${adminSuppliersPath}/supplier-details/${suppler}`;

export const adminSitesSupplierDetailsListPath = (suppler: string) => `${adminSupplierDetailsPath(suppler)}/list`;

export const adminUsersDashboardRoutePath = 'dashboard';
export const adminUsersDashboardPath = `${adminUsersPath}/${adminUsersDashboardRoutePath}`;

export const adminUsersListPath = `${adminUsersPath}/list`;

export const adminProfileRoutePath = 'profile';
export const adminProfilePath = `${baseAdminPath}/${adminProfileRoutePath}`;

export const adminErrorRoutePath = 'error';
export const adminErrorPath = `${baseAdminPath}/${adminErrorRoutePath}`;

export const adminProfilePrivacyPath = `${adminProfilePath}/privacy`;

export const adminLicensesRoutePath = 'licenses';
export const adminLicensesPath = `${baseAdminPath}/${adminLicensesRoutePath}`;

export const adminLicensesListPath = `${adminLicensesPath}/list`;

export const adminLicensesOrdersPath = `${adminLicensesPath}/orders`;

export const adminLicensesOrdersAddPath = `${adminLicensesOrdersPath}/add`;

export const adminLicensesOrderPath = (order: string) => `${adminLicensesOrdersPath}/${order}`;

export const adminLicensesEditOrderPath = (order: string) => `${adminLicensesOrderPath(order)}/edit`;

export const adminLicensesAddPath = `${adminLicensesPath}/add`;

export const adminGatewaysRoutePath = 'gateways';
export const adminGatewaysPath = `${baseAdminPath}/${adminGatewaysRoutePath}`;

export const adminGatewayPath = (gateway: string) => `${adminGatewaysPath}/${gateway}`;

export const adminGatewaysDashboardPath = `${adminGatewaysPath}/dashboard`;

export const adminGatewaysListPath = `${adminGatewaysPath}/list`;

export const adminToolboxRoutePath = 'toolbox';
export const adminToolboxPath = `${baseAdminPath}/${adminToolboxRoutePath}`;

export const adminToolboxDashboardRoutePath = 'dashboard';
export const adminToolboxDashboardPath = `${adminToolboxPath}/${adminToolboxDashboardRoutePath}`;

export const adminBulkStatusChangerRoutePath = 'balk-status-changer';
export const adminBulkStatusChangerPath = `${adminToolboxPath}/${adminBulkStatusChangerRoutePath}`;

export const adminPcbUploadRoutePath = 'pcb-upload';
export const adminPcbUploadPath = `${adminToolboxPath}/${adminPcbUploadRoutePath}`;

export const adminNotFoundRoutePath = 'not-found';
export const adminNotFoundPath = `${baseAdminPath}/${adminNotFoundRoutePath}`;

export const adminForbiddenRoutePath = 'forbidden';
export const adminForbiddenPath = `${baseAdminPath}/${adminForbiddenRoutePath}`;

export const adminServerErrorRoutePath = 'server-error';
export const adminServerErrorPath = `${baseAdminPath}/${adminServerErrorRoutePath}`;
